import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useLocation, useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import ContentLoader from "react-content-loader";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { IImages } from "@/Views/Virtual/PhoneHomePageHorisontalNew";
import {
  useDeletePointMutation,
  useSavedPointsMutation,
} from "@/features/auth/api/authAPI";
import styles from "./PhotoItemNew.module.scss";
import { useMe, useCurrentOrganization } from "@/Hooks";
import Spinner from "@/shared/ui/Spinner/Spinner";
import { produce } from "immer";
import mixpanel from "@/shared/lib/mixpanel";
import { toast } from "react-toastify";
import { SIMILAR_SCREEN_STORAGE_KEY } from "../SimilarScreen/SimilarScreenModal/SimilarScreenModal";
import {
  useSelectedScreens,
  useSelectedSearchScreens,
} from "@/entities/selectedScreens";

// Extend IImages to include is_blurry property
interface ExtendedIImages extends IImages {
  is_blurry?: boolean;
}

interface PhotoItemProps {
  isPaywallOnly?: boolean;
  setAppVideoScreens: (updateFn: (draft: IImages[]) => void) => void;
  image: ExtendedIImages; // AppXScreen object
  idVideo: number;
  handleClickPhone: (
    timestamp: number,
    videoId?: number,
    blank?: boolean,
  ) => void;
  handleImageLoad: () => void;
  onInView: (imageId: string, inView: boolean) => void;
  isActiveBookmark: boolean;
  apiDataVideosScreens: IImages[];
  handleOpenBillingPage: () => void;
  onContextMenu: (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
  ) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  refetchData: () => void;
  ScreenNotBlurredID?: number;
  appInfo?: {
    appName: string;
    appIcon: string;
    appDescription: string;
  };
}

const PhotoItemNew = ({
  isPaywallOnly,
  setAppVideoScreens,
  image,
  idVideo,
  handleClickPhone,
  handleImageLoad,
  onInView,
  isActiveBookmark,
  apiDataVideosScreens,
  handleOpenBillingPage,
  onContextMenu,
  refetchData,
  ScreenNotBlurredID,
  appInfo,
}: PhotoItemProps): React.JSX.Element => {
  const currentOrganization = useCurrentOrganization();
  const [selectedImage, setSelectedImage] = React.useState<string | undefined>(
    undefined,
  );

  const [selectedImageId, setSelectedImageId] = React.useState<null | number>(
    null,
  );
  const [imageIndex, setImageIndex] = React.useState<number | null>(null);
  const [isHover, setIsHover] = useState(false);

  const closeImageModal = () => {
    setSelectedImage(undefined);
    setImageIndex(null);
  };

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isLoading && apiDataVideosScreens) {
      setIsLoading(false);
      if (imageIndex !== null) {
        const nextIndex = imageIndex + 1;
        if (nextIndex < apiDataVideosScreens.length) {
          const nextImage = apiDataVideosScreens[nextIndex];
          setSelectedImage(nextImage.screen);
          setSelectedImageId(nextImage.id);
          setImageIndex(nextIndex);
        }
      }
    }
  }, [apiDataVideosScreens, imageIndex, isLoading]);

  const [currentModalImage, setCurrentModalImage] = useState<IImages | null>(
    null,
  );
  const [savedPoint, { isLoading: isSaving }] = useSavedPointsMutation();
  const [deletePoint, { isLoading: isDeleting }] = useDeletePointMutation();
  const [isPointActionPending, setIsPointActionPending] = useState(false);
  const [actionCooldown, setActionCooldown] = useState(false);

  const handleClick = async (event: React.MouseEvent, timestamp: string) => {
    event.stopPropagation();
    const isUserLogin = localStorage.getItem("access_token");

    if (isUserLogin) {
      if (isPointActionPending || isSaving || isDeleting || actionCooldown) {
        return;
      }

      setIsPointActionPending(true);
      setActionCooldown(true);
      const imageToSave = image;

      try {
        if (imageToSave.is_saved && imageToSave.is_saved_pk) {
          // Optimistically update UI
          const updateState = (draft: IImages[]) => {
            const index = draft.findIndex((item) => item.id === imageToSave.id);
            if (index !== -1) {
              draft[index].is_saved = false;
              draft[index].is_saved_pk = undefined;
            }
          };

          setAppVideoScreens(produce(updateState));

          setCurrentModalImage((prevImage: IImages | null) =>
            prevImage
              ? { ...prevImage, is_saved: false, is_saved_pk: undefined }
              : null,
          );

          await deletePoint(imageToSave.is_saved_pk).unwrap();

          if (!isPointActionPending) {
            try {
              await refetchData();
            } catch (error) {
              console.log("[PhotoItemNew] Refetch after remove failed", {
                error,
              });
            }
          }

          if (me?.email) {
            mixpanel.identify(me.email);
            setTimeout(() => {
              mixpanel.people.increment("total_unsaved_screens", 1);
            }, 100);
          }

          toast.success("Image removed from saved items");
        } else {
          // Save operation
          const updateStateWithId = (draft: IImages[]) => {
            const index = draft.findIndex((item) => item.id === imageToSave.id);
            if (index !== -1) {
              draft[index].is_saved = true;
            }
          };

          setAppVideoScreens(produce(updateStateWithId));

          try {
            const sp = await savedPoint({
              organizationId: currentOrganization.uuid,
              app_video: idVideo,
              ts: timestamp,
            }).unwrap();

            const updateStateWithSavedPoint = (draft: IImages[]) => {
              const index = draft.findIndex(
                (item) => item.id === imageToSave.id,
              );
              if (index !== -1) {
                draft[index].is_saved = true;
                draft[index].is_saved_pk = sp.id;
              }
            };

            setAppVideoScreens(produce(updateStateWithSavedPoint));

            setCurrentModalImage((prevImage: IImages | null) =>
              prevImage
                ? { ...prevImage, is_saved: true, is_saved_pk: sp.id }
                : null,
            );

            toast.success("Image saved successfully");

            if (!isPointActionPending) {
              try {
                await refetchData();
              } catch (error) {
                console.log("[PhotoItemNew] Refetch after save failed", {
                  error,
                });
              }
            }

            mixpanel.track("Image saved", {
              idApp: idVideo,
              app_name: image.name,
              From: "HomePage",
              idscreen: image.id,
              UserEmail: me?.email || "",
              OrganizationID: currentOrganization?.uuid || "",
            });

            if (me?.email) {
              mixpanel.identify(me.email);
              setTimeout(() => {
                mixpanel.people.increment("total_saved_screens", 1);
              }, 100);
            }
          } catch (error: any) {
            // Check for "Already saved" error
            if (error?.data?.non_field_errors?.includes("Already saved")) {
              try {
                await refetchData();
              } catch (refetchError) {
                console.log(
                  '[PhotoItemNew] Refetch after "Already saved" error failed',
                  { refetchError },
                );
              }
            }
            throw error; // Re-throw to be caught by outer catch block
          }
        }
      } catch (error: any) {
        toast.error(error.message || "Operation failed");
      } finally {
        setIsPointActionPending(false);
        setTimeout(() => setActionCooldown(false), 500);
      }
    } else {
      localStorage.removeItem("access_token");
      localStorage.setItem("btnName", "save a point");
      navigate("/login");
      window.scrollTo(0, 0);
    }
  };

  const handleNextImage = () => {
    if (imageIndex !== null && apiDataVideosScreens) {
      const nextIndex = imageIndex + 1;

      if (nextIndex >= apiDataVideosScreens.length) {
        // Loop back to the beginning
        setImageIndex(0);
        setSelectedImage(apiDataVideosScreens[0].screen);
        setSelectedImageId(apiDataVideosScreens[0].id);
        setCurrentModalImage(apiDataVideosScreens[0]);
        return;
      }

      const nextImage = apiDataVideosScreens[nextIndex];
      setSelectedImage(nextImage.screen);
      setSelectedImageId(nextImage.id);
      setImageIndex(nextIndex);
      setCurrentModalImage(nextImage);
    }
  };

  const handlePrevImage = () => {
    if (imageIndex !== null && apiDataVideosScreens) {
      const prevIndex =
        (imageIndex - 1 + apiDataVideosScreens.length) %
        apiDataVideosScreens.length;
      const prevImage = apiDataVideosScreens[prevIndex];
      setSelectedImage(prevImage.screen);
      setSelectedImageId(prevImage.id);
      setImageIndex(prevIndex);
      setCurrentModalImage(prevImage);
    }
  };

  const [isActiveSave, setIsActiveSave] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { ref: refIntersection, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
    rootMargin: "0px 100px 0px 0px",
  });

  const navigate = useNavigate();
  const { me } = useMe();

  useEffect(() => {
    onInView(image.id.toString(), inView);
  }, [inView, image.id, onInView]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage) {
        if (event.key === "ArrowRight") {
          handleNextImage();
        } else if (event.key === "ArrowLeft") {
          handlePrevImage();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, imageIndex, apiDataVideosScreens]);

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedImage(image.screen);
    setSelectedImageId(image.id);
    setImageIndex(
      apiDataVideosScreens.findIndex((item) => item.id === image.id) ?? 0,
    );
    setCurrentModalImage(image);
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    onContextMenu(event, imageId, timestamp, imageUrl);
  };

  const handleSimilarClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Determine which screen data to use (modal or main view)
    const screenToUse = currentModalImage ? currentModalImage : image;

    const similarScreenData = {
      id: screenToUse.id,
      screen: screenToUse.screen,
      appId: screenToUse.app_video,
      timestamp: screenToUse.timestamp,
      appIcon: appInfo?.appIcon,
      appName: appInfo?.appName,
      appDescription: appInfo?.appDescription,
    };

    localStorage.setItem(
      SIMILAR_SCREEN_STORAGE_KEY,
      JSON.stringify(similarScreenData),
    );

    // Close the modal if it's open
    if (selectedImage) {
      setSelectedImage(undefined);
    }

    window.open(`/similar-screens/${screenToUse.id}`, "_blank");
  };

  const location = useLocation();
  const isSearchPath = location.pathname.includes("/search");

  const searchScreens = useSelectedSearchScreens();
  const appReplayScreens = useSelectedScreens();

  // Add selection state hooks - using the appropriate hook based on path
  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = isSearchPath ? searchScreens : appReplayScreens;

  const [isSelected, setIsSelected] = useState(false);
  const [modalImageSelected, setModalImageSelected] = useState(false);

  // Handle selection for the main image view
  const handleSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (checkIsSelected(image.id)) {
      removeScreen(image.id);
      setIsSelected(false);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === image.id) {
        setModalImageSelected(false);
      }
    } else {
      if (isSearchPath) {
        // For search screens
        addScreen({
          id: image.id,
          timestamp: image.timestamp,
          ts: image.timestamp,
          is_saved: image.is_saved || false,
          is_saved_pk: image.is_saved_pk || 0,
          app_video_id: image.app_video || 0,
          app_video: image.app_video,
          isSaved: image.is_saved,
        });
      } else {
        // For regular screens
        addScreen({
          id: image.id,
          ts: image.timestamp,
          timestamp: image.timestamp,
          is_saved: image.is_saved || false,
          is_saved_pk: image.is_saved_pk || 0,
          app_video: image.app_video,
          app_video_id: image.app_video || 0,
          isSaved: image.is_saved,
        });
      }
      setIsSelected(true);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === image.id) {
        setModalImageSelected(true);
      }
    }
  };

  // Add this function to handle selection in the modal
  const handleModalSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentModalImage) return;

    if (checkIsSelected(currentModalImage.id)) {
      removeScreen(currentModalImage.id);
      setModalImageSelected(false);
      // If the modal image is the same as the main image, update main image selection state too
      if (currentModalImage.id === image.id) {
        setIsSelected(false);
      }
    } else {
      if (isSearchPath) {
        // For search screens
        addScreen({
          id: currentModalImage.id,
          timestamp: currentModalImage.timestamp,
          ts: currentModalImage.timestamp,
          is_saved: currentModalImage.is_saved || false,
          is_saved_pk: currentModalImage.is_saved_pk || 0,
          app_video_id: currentModalImage.app_video || 0,
          app_video: currentModalImage.app_video,
          isSaved: currentModalImage.is_saved,
        });
      } else {
        // For regular screens
        addScreen({
          id: currentModalImage.id,
          ts: currentModalImage.timestamp,
          timestamp: currentModalImage.timestamp,
          is_saved: currentModalImage.is_saved || false,
          is_saved_pk: currentModalImage.is_saved_pk || 0,
          app_video: currentModalImage.app_video,
          app_video_id: currentModalImage.app_video || 0,
          isSaved: currentModalImage.is_saved,
        });
      }
      setModalImageSelected(true);
      // If the modal image is the same as the main image, update main image selection state too
      if (currentModalImage.id === image.id) {
        setIsSelected(true);
      }
    }
  };

  // Add this useEffect to update both selection states when selectedScreens changes
  useEffect(() => {
    // Update main image selection state
    setIsSelected(checkIsSelected(image.id));

    // Update modal image selection state if modal is open
    if (currentModalImage) {
      setModalImageSelected(checkIsSelected(currentModalImage.id));
    }
  }, [selectedScreens, image.id, currentModalImage, checkIsSelected]);

  return (
    <div
      ref={refIntersection}
      data-image-id={image.id}
      style={{ pointerEvents: "auto" }}
      onMouseEnter={() => setIsActiveSave(true)}
      onMouseLeave={() => setIsActiveSave(false)}
      onContextMenu={(e) =>
        handleContextMenu(
          e,
          image.id.toString(),
          Number(image.timestamp),
          image.screen,
        )
      }
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          className={image.is_blurry ? styles.screenItemContainerNotLogged : ""}
        >
          <div
            className={image.is_blurry ? styles.screenItemNotLogged : ""}
            onClick={() =>
              handleClickPhone(Number(image.timestamp), image.app_video)
            }
          >
            {image.is_blurry && (
              <div className={styles.screenItemContentNotLogged}>
                <div className={styles.titleNotLogged}>
                  <p className={styles.titleNotLoggedPro}>
                    <span>Pro</span>
                  </p>
                  <p className={styles.titleNotLoggedMember}>Member's Only</p>
                </div>
                <button
                  onClick={handleOpenBillingPage}
                  className={styles.buttonNotLogged}
                >
                  <span>Unlock Pro</span>
                </button>
              </div>
            )}
            <div
              className={`${styles.screenItem} ${
                isPaywallOnly && image.label !== "paywall"
                  ? styles.paywallDimmed
                  : ""
              } ${
                ScreenNotBlurredID !== undefined &&
                ScreenNotBlurredID !== image.id
                  ? styles.paywallDimmed
                  : ""
              }`}
              onClick={handleExpandClick}
              tabIndex={0}
            >
              <img
                src={image.screen}
                alt="Screen"
                className={styles.img}
                onLoad={() => {
                  setImageLoaded(true);
                  handleImageLoad();
                }}
                style={{ display: imageLoaded ? "block" : "none" }}
                loading="eager"
              />
              {!imageLoaded && (
                <ContentLoader
                  speed={2}
                  width={254}
                  height={581.4}
                  viewBox="0 0 254 581.4"
                  backgroundColor="#f3f3f3"
                  foregroundColor="#ecebeb"
                >
                  <rect x="0" y="0" rx="7" ry="7" width="254" height="581.4" />
                </ContentLoader>
              )}
              {(isActiveSave || isSelected) && (
                <div
                  className={styles.selectButtonContainer}
                  style={{ paddingRight: "0" }}
                >
                  <div className={styles.selectButtonContainerInner}>
                    {isActiveSave && image.is_saved && (
                      <div className={styles.savedTag}>
                        <p>Saved</p>
                      </div>
                    )}

                    <button
                      className={`${styles.selectButton} ${isSelected ? styles.selected : ""}`}
                      onClick={handleSelectImage}
                    >
                      {isSelected && (
                        <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                      )}
                    </button>
                  </div>
                </div>
              )}
              {isActiveSave && (
                <div
                  className={styles.bottomMenuContainer}
                  style={{ paddingRight: "0" }}
                >
                  <div className={styles.bottomMenuContainerInner}>
                    <button onClick={handleSimilarClick}>
                      <img src="/img/similar-icon.svg" alt="similar" />
                      <p>Similar</p>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickPhone(
                          Number(image.timestamp),
                          image.app_video,
                        );
                      }}
                    >
                      <img src="/img/see-video-icon.svg" alt="see-video" />
                      <p>See Video</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!selectedImage}
        onRequestClose={closeImageModal}
        contentLabel="Image Modal"
        className={styles.modal}
        overlayClassName={styles.overlay}
      >
        <div
          className={styles.modalContent}
          onContextMenu={(e) =>
            handleContextMenu(
              e,
              image.id.toString(),
              Number(
                currentModalImage
                  ? currentModalImage.timestamp
                  : image.timestamp,
              ),
              image.screen,
            )
          }
        >
          <div
            className={styles.containerImage}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            {isLoading ? (
              <ContentLoader
                speed={2}
                width={400}
                height={800}
                viewBox="0 0 400 800"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                <rect x="0" y="0" rx="2" ry="2" width="400" height="800" />
              </ContentLoader>
            ) : (
              <img
                className={styles.modalContentPhoto}
                src={selectedImage!}
                alt="Enlarged pic"
                loading="lazy"
              />
            )}
            {(isHover || modalImageSelected) && (
              <div className={styles.modalSelectButtonContainer}>
                <div className={styles.modalSelectButtonContainerInner}>
                  {isHover &&
                    currentModalImage &&
                    currentModalImage.is_saved && (
                      <div className={styles.savedTag}>
                        <p>Saved</p>
                      </div>
                    )}

                  <button
                    className={`${styles.selectButton} ${modalImageSelected ? styles.selected : ""}`}
                    onClick={handleModalSelectImage}
                  >
                    {modalImageSelected && (
                      <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                    )}
                  </button>
                </div>
              </div>
            )}
            {isHover && (
              <div className={styles.modalBottomMenuContainer}>
                <div className={styles.modalBottomMenuContainerInner}>
                  <button onClick={handleSimilarClick}>
                    <img src="/img/similar-icon.svg" alt="similar" />
                    <p>Similar</p>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickPhone(
                        Number(
                          currentModalImage
                            ? currentModalImage.timestamp
                            : image.timestamp,
                        ),
                        image.app_video,
                      );
                    }}
                  >
                    <img src="/img/see-video-icon.svg" alt="see-video" />
                    <p>See Video</p>
                  </button>
                </div>
              </div>
            )}
          </div>
          <img
            className={styles.arrowRight}
            src="/img/whiteIcon/ChevronRight.svg"
            alt="arrowRightImg"
            onClick={handleNextImage}
            loading="lazy"
          />
          <img
            className={styles.arrowLeft}
            src="/img/whiteIcon/ChevronLeft.svg"
            alt="arrowLeftImg"
            onClick={handlePrevImage}
            loading="lazy"
          />
        </div>
      </Modal>
    </div>
  );
};

export default PhotoItemNew;
